import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import { useNavigate } from "@tanstack/react-router"
import type { FC } from "react"

import { AssociateProductType } from "../../types"

import { AkrochemProductsTable } from "./components/AkrochemProductsTable"
import { CompetitorProductsTable } from "./components/CompetitorProductsTable"
import { EmptyProductsState } from "./components/EmptyProductsState"
import { ProductSearchBar } from "./components/ProductSearchBar"
import { ProductSearchInfo } from "./components/ProductSearchInfo"
import { SelectedProductsChips } from "./components/SelectedProductsChips"
import { useAssociateProductsMutation } from "./hooks/useAssociateProductsMutation"
import { useProductSearch } from "./hooks/useProductSearch"
import { useProductSelections } from "./hooks/useProductSelections"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { MIN_SEARCH_LENGTH } from "@/constants/common"
import {
  type CompetitorProduct,
  CompetitorProductSortField,
  type Product,
  SortingOrder,
} from "@/graphql/codegen/graphql"

interface AssociateEquivalentDialogProps {
  equivalencyGroupId: string
  onClose: () => void
  onSuccess?: () => void
  associatedProductIds?: number[]
  associatedCompetitorIds?: string[]
}

export const AssociateEquivalentDialog: FC<AssociateEquivalentDialogProps> = ({
  equivalencyGroupId,
  onClose,
  onSuccess,
  associatedProductIds = [],
  associatedCompetitorIds = [],
}) => {
  const navigate = useNavigate()

  // Search & Filter State
  const {
    productType,
    searchTerm,
    productId,
    competitorProductId,
    showInactive,
    products,
    loading,
    handleTypeChange,
    handleSearchChange,
    handleToggleInactiveView,
  } = useProductSearch()

  // Selection Management
  const { selections, newSelections, cachedProducts, handleSelectionChange, handleRemoveProduct } =
    useProductSelections({
      associatedProductIds,
      associatedCompetitorIds,
    })

  // Saving changes
  const { handleSave, isSubmitting, canSave } = useAssociateProductsMutation({
    equivalencyGroupId,
    selections,
    associatedProductIds,
    associatedCompetitorIds,
    onSuccess,
    onClose,
  })

  // Local dialog actions
  const handleCreateCompetitor = () => {
    void navigate({
      to: "/app/admin/manage-products/competitor-products",
      search: {
        field: CompetitorProductSortField.ProductName,
        order: SortingOrder.Asc,
      },
    })
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const hasSearchCriteria = searchTerm.length >= MIN_SEARCH_LENGTH || productId || competitorProductId
  const showEmptyState = !loading && !products.length && hasSearchCriteria
  const currentSelected = selections[productType]

  return (
    <Dialog open onClose={handleCancel} maxWidth='lg' fullWidth>
      <DialogHeader title='Associate Equivalent' onClose={handleCancel} />

      <DialogContent classes={{ root: "p-10" }}>
        <Box className='flex flex-col gap-4'>
          <ProductSearchBar
            productType={productType}
            onTypeChange={handleTypeChange}
            onSearchChange={handleSearchChange}
          />
          <SelectedProductsChips
            productType={AssociateProductType.Akrochem}
            newSelections={newSelections[AssociateProductType.Akrochem]}
            cachedProducts={cachedProducts}
            onRemove={handleRemoveProduct}
          />
          <SelectedProductsChips
            productType={AssociateProductType.Competitor}
            newSelections={newSelections[AssociateProductType.Competitor]}
            cachedProducts={cachedProducts}
            onRemove={handleRemoveProduct}
          />

          {showEmptyState && (
            <EmptyProductsState
              productType={productType}
              searchTerm={searchTerm}
              onCreateCompetitor={handleCreateCompetitor}
            />
          )}

          {hasSearchCriteria && !showEmptyState && (
            <>
              <ProductSearchInfo
                productCount={products.length}
                productType={productType}
                showInactive={showInactive}
                onShowInactiveToggle={handleToggleInactiveView}
              />
              <Box className='max-h-96 overflow-auto'>
                {productType === AssociateProductType.Akrochem ? (
                  <AkrochemProductsTable
                    products={products as Product[]}
                    loading={loading}
                    selected={currentSelected}
                    onSelectionChange={(newSelection) => handleSelectionChange(productType, newSelection, products)}
                    associatedIds={associatedProductIds}
                  />
                ) : (
                  <CompetitorProductsTable
                    products={products as CompetitorProduct[]}
                    loading={loading}
                    selected={currentSelected}
                    onSelectionChange={(newSelection) => handleSelectionChange(productType, newSelection, products)}
                    associatedIds={associatedCompetitorIds}
                  />
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {hasSearchCriteria && !showEmptyState && (
        <DialogActions className='flex justify-between p-10'>
          <Button appearance='outlined' onClick={handleCancel} disabled={isSubmitting} data-testid='cancel-button'>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} loading={isSubmitting} data-testid='save-button'>
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}
